import React, { useState, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import InputBase from "@mui/material/InputBase";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { useTheme } from "@emotion/react";

const TagInputComponent = ({
  value,
  onChange,
  disabled = false,
  placeholder = "Add tag…",
  maxVisible = 2,
  tagLabel = "All tags",
}) => {
  const [draft, setDraft] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const badgeRef = useRef(null);
  const theme = useTheme();

  const commit = useCallback(() => {
    if (!onChange) return;
    const v = draft.trim();
    if (!v) return;
    if (value.includes(v)) {
      setDraft("");
      return;
    }
    onChange([...value, v]);
    setDraft("");
  }, [draft, onChange, value]);

  const handleKey = useCallback(
    (e) => {
      if (!onChange) return;
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        commit();
      } else if (e.key === "Backspace" && !draft && value.length > 0) {
        onChange(value.slice(0, -1));
      }
    },
    [commit, draft, onChange, value]
  );

  const isEditable = !disabled && !!onChange;
  const shouldCollapse = value.length > maxVisible;
  const visibleTags = shouldCollapse ? value.slice(0, maxVisible) : value;
  const overflowTags = shouldCollapse ? value.slice(maxVisible) : [];

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.4,
          alignItems: "center",
          p: disabled ? 0 : 0.5,
          minHeight: 32,
          borderRadius: 1,
          border: isEditable
            ? `1px solid ${theme.palette.brain.mis.grey.background1}`
            : "1px solid transparent",
          bgcolor: disabled
            ? "transparent"
            : theme.palette.brain.table.tableBackground,
          cursor: disabled ? "default" : isEditable ? "text" : "default",
          "&:focus-within": isEditable
            ? {
                borderColor: theme.palette.brain.blue.border,
                boxShadow: `0 0 0 2px ${theme.palette.brain.blue.background}`,
              }
            : {},
        }}
      >
        {visibleTags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            onDelete={
              isEditable
                ? () => onChange && onChange(value.filter((t) => t !== tag))
                : undefined
            }
            sx={{
              height: 20,
              fontSize: "0.68rem",
              fontWeight: 600,
              bgcolor: theme.palette.brain.blue.background,
              color: theme.palette.brain.blue.text,
              "& .MuiChip-deleteIcon": {
                fontSize: 14,
                color: theme.palette.brain.blue.text,
              },
            }}
          />
        ))}
        {shouldCollapse && (
          <Box
            ref={badgeRef}
            onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
            sx={{
              height: 20,
              px: 0.75,
              borderRadius: 10,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              fontSize: "0.65rem",
              fontWeight: 700,
              color: theme.palette.brain.blue.text,
              bgcolor: theme.palette.brain.blue.background,
              border: `1px dashed ${theme.palette.brain.blue.border}`,
              "&:hover": { bgcolor: theme.palette.brain.blue.border },
            }}
            aria-label={`Show ${overflowTags.length} more tags`}
          >
            +{overflowTags.length}
          </Box>
        )}
        {isEditable && (
          <InputBase
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKey}
            onBlur={commit}
            placeholder={value.length === 0 ? placeholder : ""}
            sx={{
              flex: 1,
              minWidth: 60,
              fontSize: "0.75rem",
              "& input": { p: 0 },
            }}
          />
        )}
        {!isEditable && value.length === 0 && (
          <Box
            component="span"
            sx={{ fontSize: "0.75rem", color: "text.disabled" }}
          >
            -
          </Box>
        )}
      </Box>

      {/* OPTIMIZATION: Only mount the Popover in the DOM if it is actively opened */}
      {!!anchorEl && shouldCollapse && (
        <Popover
          open={true}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            sx: {
              p: 1,
              maxWidth: 260,
              borderRadius: 1.5,
              background: theme.palette.brain.table.tableBackground,
              boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
              border: `1px solid ${theme.palette.brain.mis.grey.background1}`,
            },
          }}
          sx={{ zIndex: 10003 }}
          disableRestoreFocus
        >
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontWeight: 700,
              color: theme.palette.brain.table.row.text,
              fontSize: "0.6rem",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              mb: 0.5,
            }}
          >
            {tagLabel} ({value.length})
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
            {value.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onDelete={
                  isEditable
                    ? () => onChange && onChange(value.filter((t) => t !== tag))
                    : undefined
                }
                sx={{
                  height: 20,
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  bgcolor: theme.palette.brain.blue.background,
                  color: theme.palette.brain.blue.text,
                  "& .MuiChip-deleteIcon": {
                    fontSize: 14,
                    color: theme.palette.brain.blue.text,
                  },
                }}
              />
            ))}
          </Box>
        </Popover>
      )}
    </>
  );
};

// OPTIMIZATION: Custom equality check that ignores the inline 'onChange' function
// and strictly compares the primitive values in the array.
const arePropsEqual = (prevProps, nextProps) => {
  if (prevProps.disabled !== nextProps.disabled) return false;
  if (prevProps.maxVisible !== nextProps.maxVisible) return false;
  if (prevProps.placeholder !== nextProps.placeholder) return false;

  if (prevProps.value.length !== nextProps.value.length) return false;

  for (let i = 0; i < prevProps.value.length; i++) {
    if (prevProps.value[i] !== nextProps.value[i]) {
      return false;
    }
  }

  return true;
};

export default React.memo(TagInputComponent, arePropsEqual);
